// ===== BEGIN FILE: src/components/report-v2/sections/YearForecastSection.tsx =====
/**
 * Year at a Glance — Life Areas Forecast section.
 * Renders a rolling 12-month forecast across 8 life areas.
 *
 * ● Desktop / Print  →  SVG swim-lane bar chart (one lane per life area)
 * ● Mobile (<900px)  →  Colour-coded heat map grid (overflow-x scroll)
 *
 * Scoring: natal house strength 30 % (static) +
 *          dasha lord activation 50 %           +
 *          Jupiter / Saturn transit 20 %
 */

import React, { useMemo, Component } from 'react';
import { Box, Typography } from '@mui/joy';
import { ReportSection } from '../ReportSection';
import type { ReportSectionProps } from '../types';
import { getCurrentDashaLevels } from '../../../utils/vimshottari';
import {
  FORECAST_LIFE_AREAS,
  calcNatalScore,
  calcDashaScore,
  calcTransitScore,
  getScoreColors,
  PLANET_ABBR,
} from '../../../astro/probability/lifeAreaForecastEngine';

// ── Error Boundary ─────────────────────────────────────────────────────────────
class YearForecastErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMsg: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }
  static getDerivedStateFromError(error: any) {
    console.error('[YearForecastSection] render error caught by boundary:', error);
    return { hasError: true, errorMsg: String(error?.message ?? error) };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '16px', background: '#FEF2F2', borderRadius: '8px', margin: '8px 0' }}>
          <strong style={{ color: '#991B1B' }}>Year Forecast section could not render.</strong>
          <div style={{ fontSize: '0.75rem', color: '#7F1D1D', marginTop: '4px' }}>{this.state.errorMsg}</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Sign name → 1-indexed number ──────────────────────────────────────────────
const SIGN_TO_NUM: Record<string, number> = {
  Aries: 1, Taurus: 2, Gemini: 3, Cancer: 4, Leo: 5, Virgo: 6,
  Libra: 7, Scorpio: 8, Sagittarius: 9, Capricorn: 10, Aquarius: 11, Pisces: 12,
};

// ── SVG layout constants ───────────────────────────────────────────────────────
const LABEL_W   = 148;  // left: life area label area
const L_PAD     = 10;   // padding between label and chart
const R_PAD     = 8;
const SVG_W     = 800;
const CHART_W   = SVG_W - LABEL_W - L_PAD - R_PAD;   // ~634
const COL_W     = CHART_W / 12;
const HEADER_H  = 26;   // month name row
const LANE_H    = 34;
const LANE_GAP  = 7;
const N_AREAS   = FORECAST_LIFE_AREAS.length; // 8
const LANES_H   = N_AREAS * (LANE_H + LANE_GAP) - LANE_GAP;
const SEP_H     = 10;
const DASHA_H   = 18;
const BAND_H    = 14;  // dual-row benefic/malefic progress strip
const BOT_PAD   = 8;
const SVG_H     = HEADER_H + LANES_H + SEP_H + DASHA_H + BAND_H + BOT_PAD;
// Both Ben and Mal use the same max so bars are directly comparable:
// MAX = sum-of-weights(4.3) × base-factor(10) = 43,  scaled by actual planet strength (0-1)
const MAX_BEN   = 43.0;
const MAX_MAL   = 43.0;

// Functional nature → colour for planet labels
const FUNC_COLOR: Record<string, string> = {
  yoga_karaka: '#7C3AED',
  benefic:     '#16A34A',
  malefic:     '#DC2626',
  neutral:     '#6B7280',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getRollingMonths(): Date[] {
  const now = new Date();
  return Array.from({ length: 12 }, (_, i) =>
    new Date(now.getFullYear(), now.getMonth() + i, 15),
  );
}

const MONTH_SHORT = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short' });

const MONTH_LABEL = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

const abbr = (lord: string) => PLANET_ABBR[lord] ?? lord.slice(0, 2);

// ── Component ─────────────────────────────────────────────────────────────────
const YearForecastInner: React.FC<ReportSectionProps> = ({ data, onNavigate }) => {
  const { yearForecastInput } = data;

  // Ascendant sign number from string (e.g. "Cancer" → 4)
  const ascendantSign = SIGN_TO_NUM[data.birthDetails?.ascendant ?? ''] ?? 1;

  // Attempt to reconstruct birthISO from existing data when yearForecastInput is absent.
  // data.dashas.current.MD.start is the start of the current Mahadasha. We can reverse-
  // engineer birth time from the dasha timeline only approximately — instead, use the
  // forecast input if present, otherwise skip dasha scoring and use natal+transit only.
  const effectiveForecastInput = yearForecastInput ?? null;

  // Build planet house/sign map from report data
  const planetMap = useMemo<Record<string, { house: number; sign: number }>>(() => {
    const map: Record<string, { house: number; sign: number }> = {};
    try {
      for (const p of (data.planets ?? [])) {
        if (!p?.name) continue;
        const sign = SIGN_TO_NUM[p.sign] ?? 1;
        map[p.name] = { house: p.house ?? 1, sign };
      }
    } catch { /* ignore */ }
    return map;
  }, [data.planets]);

  // Bhava Bala map from house cusps
  const bhavaMap = useMemo<Record<number, number>>(() => {
    const map: Record<number, number> = {};
    try {
      const cusps = data.birthDetails?.houseCusps;
      if (cusps) {
        for (const c of cusps) {
          if (c?.house) map[c.house] = c.bhavaPct ?? 50;
        }
      }
    } catch { /* ignore */ }
    return map;
  }, [data.birthDetails?.houseCusps]);

  // Functional nature map: planet → 'benefic'|'malefic'|'neutral'|'yoga_karaka'
  const funcNatureMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    try {
      for (const hd of (data.birthDetails?.planetsInHouses ?? [])) {
        for (const p of (hd?.planets ?? [])) {
          if (p?.name) map[p.name] = p.functionalNature ?? 'neutral';
        }
      }
    } catch { /* ignore */ }
    return map;
  }, [data.birthDetails?.planetsInHouses]);

  // Blended planet strength map: planet → 0..1  (uses blendedPct from Shadbala/IshKashta blend)
  const planetStrengthMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    try {
      for (const hd of (data.birthDetails?.planetsInHouses ?? [])) {
        for (const p of (hd?.planets ?? [])) {
          if (p?.name) map[p.name] = Math.abs(p.blendedPct ?? 50) / 100;
        }
      }
    } catch { /* ignore */ }
    return map;
  }, [data.birthDetails?.planetsInHouses]);

  const months = useMemo(() => getRollingMonths(), []);

  // Pre-compute natal scores (fixed, one per life area)
  const natalScores = useMemo(() => {
    try {
      const hs = data.houseScores ?? {};
      return FORECAST_LIFE_AREAS.map(a => calcNatalScore(a.houses, hs, bhavaMap));
    } catch {
      return FORECAST_LIFE_AREAS.map(() => 50);
    }
  }, [data.houseScores, bhavaMap]);

  const monthData = useMemo(() => {
    try {
      const months_ = getRollingMonths();
      return months_.map(date => {
        let MD = 'Unknown', AD = 'Unknown', PD = 'Unknown', SD: string | undefined;
        let dashaText = '—';

        if (effectiveForecastInput) {
          try {
            const { birthISO, moonLon } = effectiveForecastInput;
            const levels = getCurrentDashaLevels(birthISO, moonLon, date.toISOString());
            MD = levels?.md?.lord ?? 'Unknown';
            AD = levels?.ad?.lord ?? 'Unknown';
            PD = levels?.pd?.lord ?? 'Unknown';
            SD = levels?.sd?.lord;
            dashaText = `${abbr(MD)}·${abbr(AD)}·${abbr(PD)}`;
          } catch { /* ignore dasha calc errors */ }
        }

        const scores = FORECAST_LIFE_AREAS.map((_area, i) => {
          try {
            const area = FORECAST_LIFE_AREAS[i];
            const natal   = natalScores[i] ?? 50;
            const dasha   = effectiveForecastInput
              ? calcDashaScore(area.houses, { MD, AD, PD, SD }, planetMap, ascendantSign, funcNatureMap)
              : 50;
            const transit = calcTransitScore(area.houses, date, ascendantSign, 24.14, funcNatureMap);
            return Math.max(0, Math.min(100, Math.round(natal * 0.3 + dasha * 0.5 + transit * 0.2)));
          } catch { return 50; }
        });

        // Separate benefic / malefic weighted totals — strength-scaled so a weak planet
        // contributes less than a strong one, and both bars share the same MAX scale.
        const lordWts: Array<[string, number]> = [
          [MD, 1.0], [AD, 1.5], [PD, 1.0], ...(SD ? [[SD, 0.8] as [string, number]] : []),
        ];
        const dashaInfluence = lordWts.reduce(
          (acc, [p, wt]) => {
            const n = funcNatureMap[p] ?? 'neutral';
            const str = planetStrengthMap[p] ?? 0.5;  // 0-1 from blendedPct
            const contribution = str * wt * 10;       // same base-10 for both directions
            if (n === 'yoga_karaka' || n === 'benefic') acc.benefic += contribution;
            else if (n === 'malefic') acc.malefic += contribution;
            return acc;
          },
          { benefic: 0, malefic: 0 },
        );

        return { date, MD, AD, PD, SD, dashaText, dashaInfluence, scores };
      });
    } catch (e) {
      console.error('[YearForecastSection] monthData computation failed:', e);
      return [];
    }
  }, [effectiveForecastInput, natalScores, planetMap, ascendantSign, funcNatureMap, planetStrengthMap]);

  // Power months: top 3 by average score across all life areas
  const powerMonths = useMemo(() => {
    return monthData
      .map((m, i) => ({
        idx: i,
        date: m.date,
        MD: m.MD,
        AD: m.AD,
        avg: m.scores.reduce((a, b) => a + b, 0) / (m.scores.length || 1),
      }))
      .sort((a, b) => b.avg - a.avg)
      .slice(0, 3);
  }, [monthData]);

  if (monthData.length === 0) {
    return (
      <ReportSection title="Year at a Glance — Life Areas Forecast" icon="📅" pageBreakBefore>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Forecast could not be calculated. Please regenerate the report.
        </Typography>
      </ReportSection>
    );
  }

  // ── SVG Swim-Lane Chart (desktop + print) ───────────────────────────────────
  const svgChart = (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      style={{ width: '100%', display: 'block' }}
      aria-label="Year Life Areas Forecast swim-lane chart"
    >
      {/* ── Month header row ── */}
      {months.map((d, mi) => (
        <text
          key={mi}
          x={LABEL_W + L_PAD + mi * COL_W + COL_W / 2}
          y={HEADER_H - 6}
          textAnchor="middle"
          fontSize="10"
          fontFamily="sans-serif"
          fill="#6B7280"
        >
          {MONTH_SHORT(d)}
        </text>
      ))}

      {/* ── Life-area lanes ── */}
      {FORECAST_LIFE_AREAS.map((area, ai) => {
        const laneY = HEADER_H + ai * (LANE_H + LANE_GAP);
        return (
          <g key={area.key}>
            {/* Label — use foreignObject for emoji + text alignment */}
            <text
              x={LABEL_W - 6}
              y={laneY + LANE_H / 2 + 4}
              textAnchor="end"
              fontSize="10"
              fontFamily="sans-serif"
              fill="#374151"
            >
              {area.icon} {area.label.split(' & ')[0]}
            </text>

            {/* Lane track */}
            <rect
              x={LABEL_W + L_PAD}
              y={laneY}
              width={CHART_W}
              height={LANE_H}
              fill={area.trackColor}
              rx={3}
            />

            {/* Month bars (bottom-up fill) */}
            {monthData.map((md, mi) => {
              const score = md.scores[ai];
              const { fill } = getScoreColors(score);
              const barH = Math.max(4, (score / 100) * LANE_H);
              return (
                <rect
                  key={mi}
                  x={LABEL_W + L_PAD + mi * COL_W + 1}
                  y={laneY + LANE_H - barH}
                  width={COL_W - 2}
                  height={barH}
                  fill={fill}
                  rx={2}
                  opacity={0.88}
                />
              );
            })}
          </g>
        );
      })}

      {/* ── Dasha separator ── */}
      <line
        x1={LABEL_W + L_PAD}
        y1={HEADER_H + LANES_H + SEP_H / 2}
        x2={LABEL_W + L_PAD + CHART_W}
        y2={HEADER_H + LANES_H + SEP_H / 2}
        stroke="#E5E7EB"
        strokeWidth={1}
      />

      {/* Dasha label */}
      <text
        x={LABEL_W - 6}
        y={HEADER_H + LANES_H + SEP_H + DASHA_H / 2 + 3}
        textAnchor="end"
        fontSize="9"
        fontFamily="sans-serif"
        fill="#9CA3AF"
      >
        MD·AD·PD
      </text>

      {/* ── Dasha text per month — colour-coded by functional nature ── */}
      {monthData.map((md, mi) => {
        const fc = (p: string) => FUNC_COLOR[funcNatureMap[p] ?? 'neutral'] ?? '#6B7280';
        return (
          <text
            key={mi}
            x={LABEL_W + L_PAD + mi * COL_W + COL_W / 2}
            y={HEADER_H + LANES_H + SEP_H + DASHA_H / 2 + 3}
            textAnchor="middle"
            fontSize="8"
            fontFamily="sans-serif"
          >
            <tspan fill={fc(md.MD)}>{abbr(md.MD)}</tspan>
            <tspan fill="#9CA3AF">·</tspan>
            <tspan fill={fc(md.AD)}>{abbr(md.AD)}</tspan>
            <tspan fill="#9CA3AF">·</tspan>
            <tspan fill={fc(md.PD)}>{abbr(md.PD)}</tspan>
          </text>
        );
      })}

      {/* ── Benefic / Malefic dual-row progress band ── */}
      <text x={LABEL_W - 6} y={HEADER_H + LANES_H + SEP_H + DASHA_H + 7}
        textAnchor="end" fontSize="7.5" fontFamily="sans-serif" fill="#16A34A">Ben</text>
      <text x={LABEL_W - 6} y={HEADER_H + LANES_H + SEP_H + DASHA_H + 14}
        textAnchor="end" fontSize="7.5" fontFamily="sans-serif" fill="#EF4444">Mal</text>
      {monthData.map((md, mi) => {
        const bx  = LABEL_W + L_PAD + mi * COL_W + 1;
        const bw  = COL_W - 2;
        const benW = Math.min(bw, Math.round((md.dashaInfluence.benefic / MAX_BEN) * bw));
        const malW = Math.min(bw, Math.round((md.dashaInfluence.malefic / MAX_MAL) * bw));
        const yBen = HEADER_H + LANES_H + SEP_H + DASHA_H + 2;
        const yMal = HEADER_H + LANES_H + SEP_H + DASHA_H + 9;
        return (
          <g key={mi}>
            <rect x={bx} y={yBen} width={bw}   height={5} fill="#DCFCE7" rx={1} />
            <rect x={bx} y={yBen} width={benW} height={5} fill="#16A34A" rx={1} />
            <rect x={bx} y={yMal} width={bw}   height={5} fill="#FEE2E2" rx={1} />
            <rect x={bx} y={yMal} width={malW} height={5} fill="#EF4444" rx={1} />
          </g>
        );
      })}
    </svg>
  );

  // ── Mobile Heat-Map Grid ─────────────────────────────────────────────────────
  const CELL_W = 40;
  const LABEL_COL = 108;

  const mobileGrid = (
    <Box sx={{ overflowX: 'auto', pb: 1 }}>
      <Box sx={{ minWidth: `${LABEL_COL + 12 * (CELL_W + 2)}px` }}>

        {/* Month header */}
        <Box sx={{ display: 'flex', mb: 0.75, pl: `${LABEL_COL}px` }}>
          {months.map((d, mi) => (
            <Box
              key={mi}
              sx={{ width: `${CELL_W}px`, flexShrink: 0, textAlign: 'center', fontSize: '0.6rem', color: 'text.secondary', mx: '1px' }}
            >
              {MONTH_SHORT(d)}
            </Box>
          ))}
        </Box>

        {/* Life area rows */}
        {FORECAST_LIFE_AREAS.map((area, ai) => (
          <Box key={area.key} sx={{ display: 'flex', alignItems: 'center', mb: '3px' }}>
            <Box sx={{ width: `${LABEL_COL}px`, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}>
              <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>{area.icon}</span>
              <Typography sx={{ fontSize: '0.63rem', color: 'text.primary', lineHeight: 1.25 }}>
                {area.label.split(' & ')[0]}
              </Typography>
            </Box>
            {monthData.map((md, mi) => {
              const score = md.scores[ai];
              const { bg } = getScoreColors(score);
              return (
                <Box
                  key={mi}
                  title={`${MONTH_SHORT(md.date)}: ${score}`}
                  sx={{ width: `${CELL_W}px`, height: '22px', background: bg, borderRadius: '2px', mx: '1px', flexShrink: 0 }}
                />
              );
            })}
          </Box>
        ))}

        {/* Dasha row — colour-coded by functional nature */}
        <Box sx={{ display: 'flex', mt: 1, pl: `${LABEL_COL}px` }}>
          {monthData.map((md, mi) => {
            const fc = (p: string) => FUNC_COLOR[funcNatureMap[p] ?? 'neutral'] ?? '#6B7280';
            return (
              <Box
                key={mi}
                sx={{ width: `${CELL_W}px`, flexShrink: 0, textAlign: 'center', fontSize: '0.53rem', mx: '1px', lineHeight: 1.5 }}
              >
                <span style={{ color: fc(md.MD) }}>{abbr(md.MD)}</span><br />
                <span style={{ color: fc(md.AD) }}>{abbr(md.AD)}</span><br />
                <span style={{ color: fc(md.PD) }}>{abbr(md.PD)}</span>
              </Box>
            );
          })}
        </Box>

        {/* B/M dual progress band */}
        <Box sx={{ display: 'flex', mt: '3px', pl: `${LABEL_COL}px` }}>
          {monthData.map((md, mi) => (
            <Box key={mi} sx={{ width: `${CELL_W}px`, mx: '1px', flexShrink: 0 }}>
              <Box sx={{ height: '4px', borderRadius: '1px', background: '#DCFCE7', overflow: 'hidden', mb: '2px' }}>
                <Box sx={{ height: '100%', width: `${Math.min(100, Math.round((md.dashaInfluence.benefic / MAX_BEN) * 100))}%`, background: '#16A34A' }} />
              </Box>
              <Box sx={{ height: '4px', borderRadius: '1px', background: '#FEE2E2', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.min(100, Math.round((md.dashaInfluence.malefic / MAX_MAL) * 100))}%`, background: '#EF4444' }} />
              </Box>
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <ReportSection
      title="Year at a Glance — Life Areas Forecast"
      icon="📅"
      pageBreakBefore
      onNavigate={onNavigate ? () => onNavigate('current-period') : undefined}
      navigateLabel="View Live Forecast →"
      navigateTab="current-period"
    >
      {/* Description */}
      <Box sx={{ mb: 2 }}>
        <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
          Rolling 12-month forecast across 8 key life areas. Each bar represents the strength of
          planetary support for that area in that month, combining natal house strength (30 %),
          active dasha-lord activations (50 %) and Jupiter/Saturn transits (20 %).
        </Typography>
      </Box>

      {/* Colour legend */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {[
          { fill: '#22C55E', label: 'Strong ≥70' },
          { fill: '#EAB308', label: 'Good 55–69' },
          { fill: '#F97316', label: 'Moderate 40–54' },
          { fill: '#EF4444', label: 'Caution <40' },
        ].map(({ fill, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: fill, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* SVG chart — desktop + print */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        '@media print': { display: 'block' },
      }}>
        {svgChart}
      </Box>

      {/* Heat-map grid — mobile only */}
      <Box sx={{
        display: { xs: 'block', md: 'none' },
        '@media print': { display: 'none' },
      }}>
        {mobileGrid}
      </Box>

      {/* Power Months callout */}
      <Box
        sx={{
          mt: 2.5,
          p: 2,
          background: '#F5F3FF',
          borderRadius: '10px',
          border: '1px solid #DDD6FE',
          '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
        }}
      >
        <Typography
          level="title-sm"
          sx={{ mb: 1.25, color: '#5B21B6', display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          ⭐ Peak Months — Highest Overall Support
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          {powerMonths.map((pm, rank) => (
            <Box
              key={rank}
              sx={{
                background: '#FFFFFF',
                borderRadius: '8px',
                border: '1px solid #DDD6FE',
                px: 1.5,
                py: 1,
                minWidth: '140px',
                flex: '1 1 140px',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#5B21B6' }}>
                {MONTH_LABEL(pm.date)}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#6B7280', mt: 0.3 }}>
                {pm.MD} MD · {pm.AD} AD
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#059669', fontWeight: 600, mt: 0.3 }}>
                Avg {Math.round(pm.avg)} — {pm.avg >= 65 ? 'Strong period' : pm.avg >= 55 ? 'Favourable period' : 'Moderate period'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </ReportSection>
  );
};

export const YearForecastSection: React.FC<ReportSectionProps> = (props) => (
  <YearForecastErrorBoundary>
    <YearForecastInner {...props} />
  </YearForecastErrorBoundary>
);
// ===== END FILE: src/components/report-v2/sections/YearForecastSection.tsx =====
